import type { ReadCacheServedMessage } from "~/helpers/sw/messageContract";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ReadCacheServedEntry {
	ageMs: number | null;
	cacheKey?: string;
	operationName: string;
	servedAt: number;
}

export interface ReadCacheState {
	cacheWriteSignal: number;
	refreshSignal: number;
	servedByCacheKey: Record<string, ReadCacheServedEntry>;
	servedByOperation: Record<string, ReadCacheServedEntry>;
}

export interface ReadCacheRootState {
	readCache: ReadCacheState;
}

const initialState: ReadCacheState = {
	cacheWriteSignal: 0,
	refreshSignal: 0,
	servedByCacheKey: {},
	servedByOperation: {}
};

const readCacheSlice = createSlice({
	initialState,
	name: "readCache",
	reducers: {
		bumpReadCacheWriteSignal: (state) => {
			state.cacheWriteSignal += 1;
		},
		bumpReadRefreshSignal: (state) => {
			state.refreshSignal += 1;
		},
		clearReadCacheFreshness: (state) => {
			state.servedByOperation = {};
			state.servedByCacheKey = {};
		},
		markReadCacheServed: (state, action: PayloadAction<ReadCacheServedMessage>) => {
			const { ageMs, cacheKey, operationName } = action.payload;
			const entry: ReadCacheServedEntry = {
				ageMs: ageMs ?? null,
				...(cacheKey === undefined ? {} : { cacheKey }),
				operationName,
				servedAt: Date.now()
			};
			state.servedByOperation[operationName] = entry;
			if (cacheKey !== undefined) state.servedByCacheKey[cacheKey] = entry;
		}
	}
});

export const { bumpReadCacheWriteSignal, bumpReadRefreshSignal, clearReadCacheFreshness, markReadCacheServed } =
	readCacheSlice.actions;

export const selectReadCacheServedFor =
	(operationName: string) =>
	(state: ReadCacheRootState): ReadCacheServedEntry | undefined =>
		state.readCache.servedByOperation[operationName];

export const selectReadCacheServedForKey =
	(cacheKey: string | undefined) =>
	(state: ReadCacheRootState): ReadCacheServedEntry | undefined =>
		cacheKey === undefined ? undefined : state.readCache.servedByCacheKey[cacheKey];

export const selectReadRefreshSignal = (state: ReadCacheRootState): number => state.readCache.refreshSignal;

export const selectReadCacheWriteSignal = (state: ReadCacheRootState): number => state.readCache.cacheWriteSignal;

export default readCacheSlice.reducer;
